/**
 * Patient Medications Hook
 *
 * Fetches and caches the patient's own medications (prescribed and self-reported).
 * Uses GET /{tenantId}/patients/{patientId}/medications which returns
 * patient medication records with nested medication catalog details.
 */

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useApiClient } from "../../api/louise-api";
import type { ApiMedication } from "../calendar.types";
import { patientMedicationKeys } from "../patient-cache-keys";
import { throwApiError } from "../../api/api-error";

export { patientMedicationKeys };

// ============================================================================
// Types
// ============================================================================

export type PatientMedication = ApiMedication;

// ============================================================================
// Hooks
// ============================================================================

/** Inclusive calendar-day window (YYYY-MM-DD) for a day- or range-scoped read. */
export interface PatientMedicationWindow {
  from: string;
  to: string;
}

/**
 * Cache key for the patient's medication list. A windowed read nests under the
 * list key, so every list invalidation also refreshes it, and an unwindowed
 * consumer never reads a windowed entry.
 */
export function patientMedicationsQueryKey(
  tenantId: string,
  patientId: string,
  window?: PatientMedicationWindow,
) {
  const listKey = patientMedicationKeys.list(tenantId, patientId);
  return window
    ? ([...listKey, { from: window.from, to: window.to }] as const)
    : listKey;
}

/**
 * With `window`, the server lists only medications active on at least one
 * day of it.
 */
export async function fetchPatientMedications(
  client: ReturnType<typeof useApiClient>["client"],
  tenantId: string,
  patientId: string,
  window?: PatientMedicationWindow,
): Promise<PatientMedication[]> {
  try {
    const response = await client.GET(
      "/{tenantId}/patients/{patientId}/medications",
      {
        params: {
          path: { tenantId, patientId },
          query: window ? { from: window.from, to: window.to } : undefined,
        },
      },
    );

    if (response.error) {
      throwApiError(
        response.error,
        "Failed to load patient medications",
        "patient-medications",
      );
    }

    // Handle both direct array and wrapped { data: [...] } responses
    const data = response.data;
    if (Array.isArray(data)) {
      return data as PatientMedication[];
    }
    if (data && typeof data === "object") {
      const wrapped = data as Record<string, unknown>;
      if (Array.isArray(wrapped.data)) {
        return wrapped.data as PatientMedication[];
      }
    }
    throwApiError(
      { message: "The patient medication response was not an array" },
      "Failed to load patient medications",
      "patient-medications",
    );
  } catch (error) {
    throwApiError(
      error,
      "Failed to load patient medications",
      "patient-medications",
    );
  }
}

/**
 * Hook to fetch the current patient's medications.
 * Returns medication records with nested medication catalog details.
 *
 * A windowed caller follows a moving window (the calendar month she is
 * looking at), so it holds the previous window's list while the next one
 * loads, like the appointments read beside it.
 *
 * Results are cached for 5 minutes since patient medications can change
 * during an active treatment cycle.
 */
export function usePatientMedications(
  options: { enabled?: boolean; window?: PatientMedicationWindow } = {},
) {
  const { enabled = true, window } = options;
  const { client, tenantId, patientId } = useApiClient();

  return useQuery({
    queryKey: patientMedicationsQueryKey(
      tenantId ?? "",
      patientId ?? "",
      window,
    ),
    queryFn: async () => {
      if (!tenantId || !patientId) return [];
      return fetchPatientMedications(client, tenantId, patientId, window);
    },
    enabled: enabled && !!tenantId && !!patientId,
    placeholderData: window ? keepPreviousData : undefined,
    staleTime: 1000 * 60 * 5, // 5 minutes - patient meds can change during a cycle
    gcTime: 1000 * 60 * 30, // 30 minutes cache
  });
}
